import { useState } from "react";
import api, { formatApiError } from "../api/client";
import { Plus, Trash2, GripVertical, Save } from "lucide-react";

/**
 * Admin-only: inline editor to build the exercise list for the voice-guided workout.
 * Lives inside the program folder. Persists via PATCH /api/admin/programs/{id}.
 */
export default function ExerciseListEditor({ program, onChanged }) {
  const firstWorkout = program.workouts?.[0];
  const [exercises, setExercises] = useState(firstWorkout?.exercises || []);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [dirty, setDirty] = useState(false);

  const add = () => {
    setExercises((xs) => [...xs, { name: "", sets: null, reps: "", notes: "" }]);
    setDirty(true);
  };
  const update = (i, patch) => {
    setExercises((xs) => xs.map((x, idx) => (idx === i ? { ...x, ...patch } : x)));
    setDirty(true);
  };
  const remove = (i) => {
    setExercises((xs) => xs.filter((_, idx) => idx !== i));
    setDirty(true);
  };
  const move = (i, dir) => {
    setExercises((xs) => {
      const next = [...xs];
      const j = i + dir;
      if (j < 0 || j >= next.length) return xs;
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
    setDirty(true);
  };

  const save = async () => {
    setSaving(true); setError("");
    try {
      const cleaned = exercises
        .map((e) => ({ ...e, name: (e.name || "").trim() }))
        .filter((e) => e.name);
      const workout = {
        ...(firstWorkout || { title: "Workout" }),
        id: firstWorkout?.id || crypto.randomUUID?.() || Math.random().toString(36).slice(2),
        title: firstWorkout?.title || "Workout",
        exercises: cleaned,
      };
      await api.patch(`/admin/programs/${program.id}`, { workouts: [workout] });
      setDirty(false);
      onChanged?.();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card-soft p-6" data-testid="exercise-editor">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <p className="overline">Voice-guided workout</p>
          <h3 className="font-serif text-xl text-[color:var(--ink)] mt-1">Exercise list</h3>
          <p className="text-xs text-[color:var(--ink-3)] mt-1 max-w-lg">
            Add the exercises in order. Your client hears your voice explain each one,
            then moves to the next when they say "done" or tap the button.
          </p>
        </div>
        <button
          onClick={add}
          className="btn-ghost !py-1.5 !px-3 !text-xs"
          data-testid="exercise-editor-add"
        >
          <Plus size={12} strokeWidth={1.6} /> Add exercise
        </button>
      </div>

      {exercises.length === 0 ? (
        <p className="mt-4 text-sm text-[color:var(--ink-3)] italic">
          No exercises yet. Add your first one above.
        </p>
      ) : (
        <div className="mt-5 space-y-3" data-testid="exercise-editor-list">
          {exercises.map((ex, i) => (
            <div key={i} className="rounded-2xl border border-[color:var(--line-soft)] p-4 bg-[var(--bg)] group" data-testid={`exercise-row-${i}`}>
              <div className="flex items-start gap-3">
                <div className="flex flex-col items-center gap-1 pt-1 text-[color:var(--ink-3)]">
                  <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="text-xs hover:text-[color:var(--ink)] disabled:opacity-30" title="Move up">↑</button>
                  <span className="text-[10px] font-medium">{i + 1}</span>
                  <button type="button" onClick={() => move(i, 1)} disabled={i === exercises.length - 1} className="text-xs hover:text-[color:var(--ink)] disabled:opacity-30" title="Move down">↓</button>
                </div>
                <div className="flex-1 space-y-2">
                  <input
                    className="input-warm !py-2 !text-sm"
                    placeholder="Exercise name (e.g. Barbell back squat)"
                    value={ex.name || ""}
                    onChange={(e) => update(i, { name: e.target.value })}
                    data-testid={`exercise-${i}-name`}
                  />
                  <div className="grid grid-cols-3 gap-2">
                    <input
                      className="input-warm !py-1.5 !text-xs"
                      placeholder="Sets (e.g. 3)"
                      type="number"
                      value={ex.sets || ""}
                      onChange={(e) => update(i, { sets: e.target.value ? Number(e.target.value) : null })}
                      data-testid={`exercise-${i}-sets`}
                    />
                    <input
                      className="input-warm !py-1.5 !text-xs"
                      placeholder="Reps (e.g. 8-12)"
                      value={ex.reps || ""}
                      onChange={(e) => update(i, { reps: e.target.value })}
                      data-testid={`exercise-${i}-reps`}
                    />
                    <input
                      className="input-warm !py-1.5 !text-xs"
                      placeholder="Rest (e.g. 90s)"
                      value={ex.rest || ""}
                      onChange={(e) => update(i, { rest: e.target.value })}
                      data-testid={`exercise-${i}-rest`}
                    />
                  </div>
                  <textarea
                    className="input-warm !py-1.5 !text-xs"
                    rows={2}
                    placeholder="Coaching notes (optional — what to focus on, what to avoid)"
                    value={ex.notes || ""}
                    onChange={(e) => update(i, { notes: e.target.value })}
                    data-testid={`exercise-${i}-notes`}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => remove(i)}
                  className="text-[color:var(--ink-3)] hover:text-[color:var(--accent-2)] mt-1"
                  data-testid={`exercise-${i}-remove`}
                  title="Remove"
                >
                  <Trash2 size={14} strokeWidth={1.5} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {error && (
        <p className="mt-3 text-sm text-[color:var(--accent-2)]" data-testid="exercise-editor-error">{error}</p>
      )}

      {dirty && (
        <div className="mt-5 flex items-center justify-end gap-3 pt-4 border-t border-[color:var(--line-soft)]">
          <p className="text-xs text-[color:var(--ink-3)]">Unsaved changes</p>
          <button
            onClick={save}
            disabled={saving}
            className="btn-primary !py-2 !px-4 !text-sm"
            data-testid="exercise-editor-save"
          >
            <Save size={13} strokeWidth={1.6} /> {saving ? "Saving…" : "Save exercise list"}
          </button>
        </div>
      )}
    </div>
  );
}
